/**
 * Document to Position Utility
 *
 * Creates or updates position metrics from extracted document data.
 * Maps document fields to position_metrics table columns based on document type.
 */

import { supabase } from "@/integrations/supabase/client";

export interface DocumentData {
  // Identification
  fund_name?: string;
  gp_fund_manager?: string;
  investor_entity?: string;
  currency?: string;

  // Financial - Capital Calls
  capital_commitment?: number;
  call_amount?: number;
  call_percentage?: number;
  remaining_commitment?: number;
  total_capital_called?: number;

  // Financial - Distributions
  distribution_amount?: number;
  gross_distribution?: number;
  total_distributions?: number;
  total_recallable_distributions?: number;

  // Financial - Account/NAV
  capital_balance?: number;
  total_contributions?: number;
  net_asset_value?: number;
  total_partners_capital?: number;

  // Dates
  valuation_date?: string;
  report_date?: string;
  statement_date?: string;
  period_end?: string;
  notice_date?: string;
  due_date?: string;

  // Catch-all for other fields
  [key: string]: unknown;
}

export interface CreatePositionFromDocumentInput {
  orgId: string;
  familyId: string | null;
  instrumentId: string;
  documentId: string;
  documentType: string;
  data: DocumentData;
  sourceDocPage?: number;
}

export interface PositionResult {
  positionId: string;
  metricsId: string;
  isNewPosition: boolean;
}

/**
 * Extract period end date from document data
 */
export function extractPeriodEnd(data: DocumentData, documentType: string): string {
  // Try various date fields in order of preference
  if (data.valuation_date) return data.valuation_date;
  if (data.report_date) return data.report_date;
  if (data.period_end) return data.period_end;
  if (data.statement_date) return data.statement_date;
  if (data.notice_date) return data.notice_date;
  if (data.due_date) return data.due_date;

  // Default to today's date if no date found
  return new Date().toISOString().split("T")[0];
}

/**
 * Map document data to position_metrics fields based on document type
 */
export function mapDocumentToMetrics(
  data: DocumentData,
  documentType: string
): Record<string, unknown> {
  const metrics: Record<string, unknown> = {};

  switch (documentType) {
    case "quarterly_financial_statement":
    case "quarterly_account_statement":
      // NAV and account balance data
      if (data.net_asset_value != null) metrics.nav_base = data.net_asset_value;
      if (data.capital_balance != null) metrics.mv_base = data.capital_balance;
      if (data.total_partners_capital != null) {
        metrics.mv_base = data.total_partners_capital;
      }

      // Commitment and capital flows
      if (data.capital_commitment != null) {
        metrics.commitment_base = data.capital_commitment;
      }
      if (data.total_contributions != null || data.total_capital_called != null) {
        metrics.paid_in_base =
          data.total_contributions ?? data.total_capital_called;
      }
      if (data.total_distributions != null) {
        metrics.distributed_base = data.total_distributions;
      }
      if (data.remaining_commitment != null) {
        metrics.unfunded_base = data.remaining_commitment;
      }
      if (data.total_recallable_distributions != null) {
        metrics.recallable_base = data.total_recallable_distributions;
      }
      break;

    case "capital_call":
      // Capital call updates paid-in capital incrementally
      // Note: We record the call amount, but actual metrics update
      // should aggregate all calls for the position
      if (data.call_amount != null) {
        // Store call amount in metadata, actual paid_in calculated separately
        metrics.paid_in_base = data.total_capital_called ?? data.call_amount;
      }
      if (data.capital_commitment != null) {
        metrics.commitment_base = data.capital_commitment;
      }
      if (data.remaining_commitment != null) {
        metrics.unfunded_base = data.remaining_commitment;
      }
      break;

    case "distribution_notice":
      // Distribution updates distributed capital
      if (data.distribution_amount != null || data.gross_distribution != null) {
        // Prefer gross if available, otherwise net
        metrics.distributed_base =
          data.total_distributions ??
          data.gross_distribution ??
          data.distribution_amount;
      }
      break;

    default:
      // Unknown document type - try to map common fields
      if (data.net_asset_value != null) metrics.nav_base = data.net_asset_value;
      if (data.capital_commitment != null) {
        metrics.commitment_base = data.capital_commitment;
      }
      break;
  }

  return metrics;
}

/**
 * Find or create a position for the given instrument and family
 */
async function findOrCreatePosition(
  orgId: string,
  familyId: string | null,
  instrumentId: string
): Promise<{ positionId: string; isNew: boolean }> {
  // Try to find existing open position
  const { data: existingPositions } = await supabase
    .from("positions")
    .select("id")
    .eq("org_id", orgId)
    .eq("instrument_id", instrumentId)
    .is("close_date", null);

  // If family-specific, filter by family
  if (familyId && existingPositions) {
    const familyPosition = existingPositions.find(async (p) => {
      const { data } = await supabase
        .from("positions")
        .select("family_id")
        .eq("id", p.id)
        .single();
      return data?.family_id === familyId;
    });

    if (familyPosition) {
      return { positionId: familyPosition.id, isNew: false };
    }
  } else if (existingPositions && existingPositions.length > 0) {
    return { positionId: existingPositions[0].id, isNew: false };
  }

  // Create new position
  const { data: newPosition, error } = await supabase
    .from("positions")
    .insert({
      org_id: orgId,
      family_id: familyId,
      instrument_id: instrumentId,
      open_date: new Date().toISOString().split("T")[0],
    })
    .select("id")
    .single();

  if (error || !newPosition) {
    throw new Error(`Failed to create position: ${error?.message}`);
  }

  return { positionId: newPosition.id, isNew: true };
}

/**
 * Create or update position and metrics from extracted document data
 */
export async function createPositionFromDocument(
  input: CreatePositionFromDocumentInput
): Promise<PositionResult> {
  const { orgId, familyId, instrumentId, documentId, documentType, data, sourceDocPage } =
    input;

  // Find or create position
  const { positionId, isNew } = await findOrCreatePosition(
    orgId,
    familyId,
    instrumentId
  );

  // Extract period end date
  const periodEnd = extractPeriodEnd(data, documentType);

  // Map document data to metrics
  const metricsData = mapDocumentToMetrics(data, documentType);

  // Upsert position metrics
  const { data: metrics, error: metricsError } = await supabase
    .from("position_metrics")
    .upsert(
      {
        org_id: orgId,
        family_id: familyId,
        position_id: positionId,
        period_end: periodEnd,
        source_doc_id: documentId,
        source_page: sourceDocPage,
        source: "document_extraction",
        ...metricsData,
      },
      {
        onConflict: "position_id,period_end",
      }
    )
    .select("id")
    .single();

  if (metricsError) {
    throw new Error(`Failed to create position metrics: ${metricsError.message}`);
  }

  return {
    positionId,
    metricsId: metrics?.id ?? "",
    isNewPosition: isNew,
  };
}

/**
 * Link a document to an instrument (updates documents table)
 */
export async function linkDocumentToInstrument(
  documentId: string,
  instrumentId: string
): Promise<void> {
  const { error } = await supabase
    .from("documents")
    .update({ instrument_id: instrumentId })
    .eq("id", documentId);

  if (error) {
    throw new Error(`Failed to link document to instrument: ${error.message}`);
  }
}

/**
 * Get all position metrics for a document
 */
export async function getDocumentPositionMetrics(
  documentId: string
): Promise<Array<{
  id: string;
  position_id: string;
  period_end: string;
  nav_base: number | null;
  commitment_base: number | null;
  paid_in_base: number | null;
  distributed_base: number | null;
}>> {
  const { data, error } = await supabase
    .from("position_metrics")
    .select("id, position_id, period_end, nav_base, commitment_base, paid_in_base, distributed_base")
    .eq("source_doc_id", documentId);

  if (error) {
    throw new Error(`Failed to get position metrics: ${error.message}`);
  }

  return data ?? [];
}
